// Guest progressive fit collector for the PurchaseConfidenceCard. Renders
// pills for size → height → body_shape → body_size (women) / body_build (men),
// a silhouette preview once height is known, reasoning rows from
// generateReasoning(), and a "completed steps" row with "change" affordance.
//
// All values write through to sessionStorage (l2f_guest_size for the size
// field, l2f_guest_model for the rest) so they survive a page refresh and are
// picked up at signup time by the migration block in src/routes/index.tsx.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// LookbookLoadingSilhouette moved to PurchaseConfidenceCard with the unified CTA.
import {
  BODY_BUILD_OPTIONS_MEN,
  BODY_SHAPE_OPTIONS_BY_GENDER,
  BODY_SIZE_OPTIONS_WOMEN,
  GUEST_MODEL_STORAGE_KEY,
  HEIGHT_OPTIONS_BY_GENDER,
  TOP_SIZE_OPTIONS,
  readGuestModelProfile,
  writeGuestModelProfile,
  type FieldOption,
  type GuestModelProfile,
  type HeightValue,
} from "@/lib/profileFieldOptions";
import {
  generateReasoning,
  getConfidenceLevel,
  type ConfidenceAssessment,
  type ConfidenceLevel,
  type Gender,
  type MissingField,
  type ReasoningRow,
} from "@/lib/guestReasoningTemplates";
import { trackEvent } from "@/lib/analytics";

const CORAL_BG = "var(--l2f-cream)";
const CORAL_BORDER = "#C84B2F";
const CORAL_INK = "var(--l2f-danger-ink)";
const CORAL_CTA = "var(--l2f-coral)";
const PILL_BORDER = "rgba(0,0,0,0.12)";

const SIZE_STORAGE_KEY = "l2f_guest_size";
const SIZE_NUMBER_STORAGE_KEY = "l2f_guest_size_number";
const SIZE_NUMBER_SEEN_KEY = "l2f_guest_size_number_seen";

const UK_NUMBER_OPTIONS = ["6", "8", "10", "12", "14", "16", "18", "20"];

type Step = "size" | "size_number" | "height" | "body_shape" | "body_size" | "complete";

export type GuestRegenStatus = "idle" | "loading" | "success" | "error";

import { detectTierConventions, type TierLite } from "@/lib/sizeChartResolver";

export interface GuestProgressiveFitProps {
  gender: Gender | null;
  outfitId: string | null;
  fitType: string | null;
  fabricComposition: string | null;
  category: string | null;
  sizeInChart: boolean;
  retailerName: string | null;
  /**
   * Build B follow-up: per-tier retailer + size_chart info used to detect
   * which size conventions the active outfit requires. Drives whether the
   * numeric UK size step is rendered.
   */
  heroTiers?: TierLite[];
  /**
   * Unified confidence assessment from the parent (Build 3e). When provided,
   * its `reasoning_rows` + `confidence_level` are the source of truth for the
   * inline fit panel — this prevents the legacy in-component reasoning from
   * showing a different confidence value than the parent recommendation card.
   */
  assessment?: ConfidenceAssessment | null;
  /**
   * Build 3f: parent (PurchaseConfidenceCard) bumps the nonce when the user
   * taps the diagnostic message in the recommendation section. The child
   * then reverts to that step and scrolls it into view.
   */
  focusFieldRequest?: { field: MissingField["field"]; nonce: number } | null;
  onProfileChange?: (next: {
    size: string | null;
    sizeNumber: string | null;
    model: GuestModelProfile;
  }) => void;
  onRegenerate?: (model: GuestModelProfile, size: string | null) => void;
  regenStatus?: GuestRegenStatus;
  regenError?: string | null;
}

function normaliseGender(g: Gender | null): "women" | "men" | "non-binary" {
  if (g === "men") return "men";
  if (g === "non-binary") return "non-binary";
  return "women";
}

function readGuestSize(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SIZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { top_letter?: string | null };
    return parsed.top_letter ?? null;
  } catch {
    return null;
  }
}

function writeGuestSize(top_letter: string, gender: Gender | null): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.sessionStorage.getItem(SIZE_STORAGE_KEY);
    const current = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const g = gender === "men" ? "men" : "women";
    const next = { ...current, gender: g, top_letter };
    window.sessionStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function readGuestSizeNumber(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(SIZE_NUMBER_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeGuestSizeNumber(value: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      window.sessionStorage.setItem(SIZE_NUMBER_STORAGE_KEY, value);
      // Also stash inside the unified size object so signup migration sees it.
      const raw = window.sessionStorage.getItem(SIZE_STORAGE_KEY);
      const current = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
      window.sessionStorage.setItem(
        SIZE_STORAGE_KEY,
        JSON.stringify({ ...current, top_uk_number: value }),
      );
    } else {
      window.sessionStorage.removeItem(SIZE_NUMBER_STORAGE_KEY);
    }
    window.sessionStorage.setItem(SIZE_NUMBER_SEEN_KEY, "1");
  } catch {
    /* ignore */
  }
}

function readSizeNumberSeen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SIZE_NUMBER_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function iconFor(icon: ReasoningRow["icon"]): string {
  return icon === "check" ? "✓" : icon === "warning" ? "⚠" : "ℹ";
}

function iconColor(icon: ReasoningRow["icon"]): string {
  return icon === "check"
    ? "var(--l2f-success-ink-soft)"
    : icon === "warning"
      ? "var(--l2f-warning-ink-soft)"
      : "var(--muted-foreground)";
}

function confidenceCopy(
  level: ConfidenceLevel | "high",
): { label: string; color: string } {
  if (level === "high") {
    return { label: "High confidence", color: "var(--l2f-success-ink-soft)" };
  }
  if (level === "medium-high") {
    return { label: "Medium-high confidence", color: "var(--l2f-success-ink-soft)" };
  }
  if (level === "medium") {
    return { label: "Medium confidence", color: "var(--l2f-warning-ink-soft)" };
  }
  return { label: "General guidance", color: "var(--muted-foreground)" };
}

function determineStep(
  size: string | null,
  model: GuestModelProfile,
  gender: Gender | null,
  needsNumeric: boolean,
  sizeNumberSeen: boolean,
): Step {
  if (!size) return "size";
  // Numeric step inserted only when active outfit's tier conventions require it.
  if (needsNumeric && !sizeNumberSeen) return "size_number";
  if (!model.height) return "height";
  if (!model.body_shape) return "body_shape";
  if (gender === "men") return "complete";
  if (!model.body_size) return "body_size";
  return "complete";
}

export default function GuestProgressiveFit({
  gender,
  outfitId,
  fitType,
  fabricComposition,
  category,
  sizeInChart,
  retailerName,
  heroTiers,
  assessment = null,
  focusFieldRequest = null,
  onProfileChange,
  onRegenerate,
  regenStatus = "idle",
  regenError = null,
}: GuestProgressiveFitProps) {
  const effectiveGender = normaliseGender(gender);
  const mountedAtRef = useRef<number>(Date.now());
  const currentStepRef = useRef<HTMLDivElement | null>(null);

  // Hydrate from sessionStorage on mount and whenever the outfit changes
  // (the card lives across outfit changes in the parent).
  const [size, setSize] = useState<string | null>(null);
  const [sizeNumber, setSizeNumber] = useState<string | null>(null);
  const [sizeNumberSeen, setSizeNumberSeen] = useState<boolean>(false);
  const [model, setModel] = useState<GuestModelProfile>({
    height: null,
    body_shape: null,
    body_size: null,
  });

  useEffect(() => {
    setSize(readGuestSize());
    setSizeNumber(readGuestSizeNumber());
    setSizeNumberSeen(readSizeNumberSeen());
    setModel(
      readGuestModelProfile() ?? { height: null, body_shape: null, body_size: null },
    );
  }, []);

  // Keep sessionStorage in sync across tabs (cheap defensive listener).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === SIZE_STORAGE_KEY) setSize(readGuestSize());
      if (e.key === GUEST_MODEL_STORAGE_KEY) {
        setModel(
          readGuestModelProfile() ?? {
            height: null,
            body_shape: null,
            body_size: null,
          },
        );
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const previousLevelRef = useMemo<{ current: ConfidenceLevel | "high" }>(
    () => ({ current: getConfidenceLevel({ ...model, size, gender }) }),
    // intentionally only computed on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Build B follow-up: detect which size conventions the active outfit needs.
  const { conventions_needed, sources: conventionSources } = useMemo(
    () => detectTierConventions(heroTiers ?? []),
    [heroTiers],
  );
  const needsLetter = conventions_needed.includes("letter");
  const needsNumeric = conventions_needed.includes("numeric");

  const step = determineStep(size, model, gender, needsNumeric, sizeNumberSeen);

  // Observability + analytics for adaptive form behaviour.
  const loggedShownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!heroTiers || heroTiers.length === 0) return;
    const key = `${outfitId}|${needsLetter ? "L" : ""}${needsNumeric ? "N" : ""}`;
    if (loggedShownRef.current === key) return;
    loggedShownRef.current = key;
    try {
      console.log(
        `[guest-form] tier conventions: needed=${conventions_needed.join(",")} sources=${JSON.stringify(conventionSources)}`,
      );
    } catch {
      /* ignore */
    }
    trackEvent("guest_progressive_fit_shown", {
      letter_field_shown: needsLetter,
      numeric_field_shown: needsNumeric,
      detection_sources: conventionSources,
      outfit_id: outfitId,
    });
  }, [
    heroTiers,
    needsLetter,
    needsNumeric,
    conventions_needed,
    conventionSources,
    gender,
    outfitId,
  ]);

  // Prefer the unified assessment from parent (Build 3e). Falls back to the
  // local heuristic only when no assessment is supplied (legacy callers).
  const level: ConfidenceLevel | "high" =
    assessment?.confidence_level ??
    getConfidenceLevel({ ...model, size, gender });

  // Fire upgrade analytics
  useEffect(() => {
    if (previousLevelRef.current !== level) {
      trackEvent("confidence_card_level_upgraded", {
        user_type: "guest",
        from_level: previousLevelRef.current,
        to_level: level,
        outfit_id: outfitId,
      });
      previousLevelRef.current = level;
    }
  }, [level, outfitId, previousLevelRef]);

  // Notify parent (used to swap thumb → silhouette in the hero header).
  useEffect(() => {
    onProfileChange?.({ size, sizeNumber, model });
  }, [size, sizeNumber, model, onProfileChange]);

  const handleSize = useCallback(
    (s: string) => {
      writeGuestSize(s, gender);
      setSize(s);
      trackEvent("confidence_card_size_tapped", {
        user_type: "guest",
        size_selected: s,
        outfit_id: outfitId,
      });
    },
    [gender, outfitId],
  );

  const handleSizeNumber = useCallback(
    (value: string | null) => {
      writeGuestSizeNumber(value);
      setSizeNumber(value);
      setSizeNumberSeen(true);
      trackEvent("guest_progressive_fit_completed", {
        letter_provided: !!size,
        numeric_provided: !!value,
        outfit_id: outfitId,
      });
    },
    [size, outfitId],
  );

  const handleHeight = useCallback(
    (h: HeightValue) => {
      const next: GuestModelProfile = {
        ...model,
        height: h,
        // clear later steps if user is rewinding
        body_shape: model.height === h ? model.body_shape : model.body_shape,
        body_size: model.height === h ? model.body_size : model.body_size,
      };
      writeGuestModelProfile({ height: h });
      setModel(next);
      trackEvent("confidence_card_height_tapped", {
        user_type: "guest",
        height_selected: h,
        outfit_id: outfitId,
      });
    },
    [model, outfitId],
  );

  const handleShape = useCallback(
    (s: string) => {
      writeGuestModelProfile({ body_shape: s });
      setModel((m) => ({ ...m, body_shape: s }));
      trackEvent("confidence_card_shape_tapped", {
        user_type: "guest",
        shape_selected: s,
        outfit_id: outfitId,
      });
    },
    [outfitId],
  );

  const handleBodySize = useCallback(
    (s: string) => {
      writeGuestModelProfile({ body_size: s });
      setModel((m) => ({ ...m, body_size: s }));
      trackEvent("confidence_card_size_body_tapped", {
        user_type: "guest",
        body_size_selected: s,
        outfit_id: outfitId,
      });
    },
    [outfitId],
  );

  // Revert to a step by clearing it and everything after.
  const revertTo = useCallback(
    (target: Step) => {
      if (target === "size") {
        // clearing size: nuke size only; model stays so user doesn't lose it
        try {
          const raw = window.sessionStorage.getItem(SIZE_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            delete parsed.top_letter;
            window.sessionStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(parsed));
          }
        } catch {
          /* ignore */
        }
        setSize(null);
        return;
      }
      if (target === "height") {
        writeGuestModelProfile({ height: null, body_shape: null, body_size: null });
        setModel({ height: null, body_shape: null, body_size: null });
        return;
      }
      if (target === "body_shape") {
        writeGuestModelProfile({ body_shape: null, body_size: null });
        setModel((m) => ({ ...m, body_shape: null, body_size: null }));
        return;
      }
      if (target === "body_size") {
        writeGuestModelProfile({ body_size: null });
        setModel((m) => ({ ...m, body_size: null }));
        return;
      }
      if (target === "size_number") {
        try {
          window.sessionStorage.removeItem(SIZE_NUMBER_STORAGE_KEY);
          window.sessionStorage.removeItem(SIZE_NUMBER_SEEN_KEY);
          const raw = window.sessionStorage.getItem(SIZE_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            delete parsed.top_uk_number;
            window.sessionStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(parsed));
          }
        } catch {
          /* ignore */
        }
        setSizeNumber(null);
        setSizeNumberSeen(false);
        return;
      }
    },
    [],
  );

  // Build 3f: react to diagnostic taps from the parent recommendation section.
  // The parent bumps `focusFieldRequest.nonce`; we revert to that step (so the
  // pill row shows) and scroll it into view.
  const lastFocusNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!focusFieldRequest) return;
    if (lastFocusNonceRef.current === focusFieldRequest.nonce) return;
    lastFocusNonceRef.current = focusFieldRequest.nonce;
    // Map MissingField slot → Step.
    let target: Step;
    if (focusFieldRequest.field === "body_build") target = "body_shape";
    else if (focusFieldRequest.field === "size_number") target = "size_number";
    else target = focusFieldRequest.field as Step;
    revertTo(target);
    requestAnimationFrame(() => {
      currentStepRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [focusFieldRequest, revertTo]);


  const heightOptions = HEIGHT_OPTIONS_BY_GENDER[effectiveGender];
  const shapeOptions: FieldOption[] =
    effectiveGender === "men"
      ? BODY_BUILD_OPTIONS_MEN
      : BODY_SHAPE_OPTIONS_BY_GENDER[effectiveGender];

  const legacyReasoning = generateReasoning({
    fit_type: fitType,
    fabric_composition: fabricComposition,
    category,
    size_in_chart: sizeInChart,
    size,
    height: model.height,
    body_shape: model.body_shape,
    body_size: model.body_size,
    gender,
    retailer_name: retailerName,
  });
  // Reasoning rows come from the unified assessment when present, so the
  // confidence/rows shown here match the parent recommendation card.
  // based_on/limited_by still come from the legacy helper (it describes
  // guest profile completeness, which is local to this collector).
  const reasoning = {
    rows: assessment?.reasoning_rows ?? legacyReasoning.rows,
    based_on: legacyReasoning.based_on,
    limited_by: legacyReasoning.limited_by,
  };

  const completedRows = useMemo(() => {
    const entries: { key: Step; label: string; value: string }[] = [];
    if (size) entries.push({ key: "size", label: "Size", value: size });
    if (sizeNumber && effectiveGender !== "men") {
      entries.push({ key: "size_number", label: "UK size", value: sizeNumber });
    }
    if (model.height) {
      const label =
        heightOptions.find((o) => o.value === model.height)?.label ?? model.height;
      entries.push({ key: "height", label: "Height", value: label });
    }
    if (model.body_shape) {
      const label =
        shapeOptions.find((o) => o.value === model.body_shape)?.label ??
        model.body_shape;
      entries.push({
        key: "body_shape",
        label: effectiveGender === "men" ? "Build" : "Shape",
        value: label,
      });
    }
    if (model.body_size && effectiveGender !== "men") {
      const label =
        BODY_SIZE_OPTIONS_WOMEN.find((o) => o.value === model.body_size)?.label ??
        model.body_size;
      entries.push({ key: "body_size", label: "Build", value: label });
    }
    return entries;
  }, [size, model, heightOptions, shapeOptions, effectiveGender]);

  // Build 3f: confidence label removed; `level` is still computed above for
  // analytics (confidence_card_level_upgraded). confidenceCopy() helper kept
  // for potential future use but no longer rendered here.
  void confidenceCopy;

  return (
    <div
      style={{
        marginTop: 16,
        background: "#FAFAF7",
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: 12,
        padding: 12,
      }}
    >
      {/* Build 3f: confidence label removed — the cream recommendation
          section above (PurchaseConfidenceCard) is the single source of
          confidence on the card. Reasoning rows below remain as evidence. */}


      {/* Completed steps row */}
      {completedRows.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 10,
          }}
        >
          {completedRows.map((row) => {
            const disabled = regenStatus === "loading";
            return (
              <button
                key={row.key}
                type="button"
                onClick={() => !disabled && revertTo(row.key)}
                disabled={disabled}
                style={{
                  background: "white",
                  border: `0.5px solid ${PILL_BORDER}`,
                  borderRadius: 999,
                  padding: "4px 10px",
                  minHeight: 44,
                  fontSize: 12,
                  color: "var(--foreground)",
                  cursor: disabled ? "default" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  fontFamily: "var(--font-body)",
                }}
                aria-label={`Change ${row.label}`}
              >
                <span style={{ color: "var(--muted-foreground)" }}>{row.label}:</span>{" "}
                <span style={{ fontWeight: 600 }}>{row.value}</span>
                {!disabled && (
                  <span
                    style={{
                      color: "var(--muted-foreground)",
                      marginLeft: 6,
                      fontSize: 12,
                    }}
                  >
                    change
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Reasoning rows */}
      {reasoning.rows.length > 0 && (
        <ul style={{ margin: "0 0 10px", padding: 0, listStyle: "none" }}>
          {reasoning.rows.map((r, i) => (
            <li
              key={i}
              className="l2f-type-secondary"
              style={{
                color: "var(--foreground)",
                marginTop: i === 0 ? 0 : 4,
                display: "flex",
                gap: 6,
                alignItems: "flex-start",
              }}
            >
              <span style={{ color: iconColor(r.icon), flexShrink: 0 }}>
                {iconFor(r.icon)}
              </span>
              <span>{r.text}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Loading silhouette + primary CTA moved to PurchaseConfidenceCard bottom (single CTA). */}



      {/* Success confirmation */}
      {regenStatus === "success" && (
        <div
          className="l2f-type-secondary"
          style={{
            marginTop: 10,
            padding: "10px 12px",
            background: "var(--l2f-success-bg)",
            border: "0.5px solid var(--l2f-success-bg)",
            borderRadius: 10,
            color: "var(--l2f-success-ink-soft)",
          }}
        >
          ✓ Lookbooks updated with your profile
        </div>
      )}

      {/* Current-step pill row */}
      {regenStatus !== "loading" && step !== "complete" && step !== "size_number" && (
        <div ref={currentStepRef} style={{ marginTop: 6 }}>
          <p
            className="l2f-type-section-header"
            style={{
              color: "var(--muted-foreground)",
              margin: "0 0 6px",
            }}
          >
            {step === "size" && "Your usual size"}
            {step === "height" && "Your height"}
            {step === "body_shape" &&
              (effectiveGender === "men" ? "Your build" : "Your shape")}
            {step === "body_size" && "Your build"}
          </p>
          <PillRow
            options={
              step === "size"
                ? TOP_SIZE_OPTIONS.map((v) => ({ value: v, label: v }))
                : step === "height"
                  ? heightOptions
                  : step === "body_shape"
                    ? shapeOptions
                    : BODY_SIZE_OPTIONS_WOMEN
            }
            selected={
              step === "size"
                ? null
                : step === "height"
                  ? model.height
                  : step === "body_shape"
                    ? model.body_shape
                    : model.body_size
            }
            onSelect={(v) => {
              if (step === "size") handleSize(v);
              else if (step === "height") handleHeight(v as HeightValue);
              else if (step === "body_shape") handleShape(v);
              else handleBodySize(v);
            }}
          />
        </div>
      )}

      {/* Build B: numeric UK size step — shown only when tier conventions need it. */}
      {regenStatus !== "loading" && step === "size_number" && (
        <div ref={currentStepRef} style={{ marginTop: 6 }}>
          <p
            className="l2f-type-section-header"
            style={{
              color: "var(--muted-foreground)",
              margin: "0 0 4px",
            }}
          >
            Your UK number size
          </p>
          <p
            className="l2f-type-caption"
            style={{
              color: "var(--muted-foreground)",
              margin: "0 0 8px",
            }}
          >
            {needsLetter
              ? "Some of these brands use numbers. Skip if unsure."
              : "These brands use number sizes (8, 10, 12, 14)."}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {UK_NUMBER_OPTIONS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => handleSizeNumber(v)}
                style={{
                  background: "white",
                  border: `0.5px solid ${PILL_BORDER}`,
                  color: "var(--foreground)",
                  borderRadius: 999,
                  padding: "6px 12px",
                  minHeight: 44,
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                }}
              >
                {v}
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleSizeNumber(null)}
              style={{
                background: "transparent",
                border: `0.5px dashed ${PILL_BORDER}`,
                color: "var(--muted-foreground)",
                borderRadius: 999,
                padding: "6px 12px",
                minHeight: 44,
                fontSize: 15,
                cursor: "pointer",
                fontFamily: "var(--font-body)",
              }}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Primary CTA moved to PurchaseConfidenceCard bottom (single unified CTA). */}



      {/* Based on / limited by footer */}
      <p
        className="l2f-type-caption"
        style={{
          color: "var(--muted-foreground)",
          marginTop: 10,
          marginBottom: 0,
        }}
      >
        Based on: {reasoning.based_on}
      </p>
      {reasoning.limited_by && (
        <p
          className="l2f-type-caption"
          style={{
            color: "var(--muted-foreground)",
            marginTop: 2,
            marginBottom: 0,
          }}
        >
          Limited by: {reasoning.limited_by}
        </p>
      )}
    </div>
  );
}

function PillRow({
  options,
  selected,
  onSelect,
}: {
  options: FieldOption[];
  selected: string | null;
  onSelect: (value: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((o) => {
        const isSelected = selected === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onSelect(o.value)}
            aria-pressed={isSelected}
            style={{
              background: isSelected ? CORAL_BG : "white",
              border: `0.5px solid ${isSelected ? CORAL_BORDER : PILL_BORDER}`,
              color: isSelected ? CORAL_INK : "var(--foreground)",
              borderRadius: 999,
              padding: "6px 12px",
              minHeight: 44,
              fontSize: 15,
              fontWeight: isSelected ? 600 : 500,
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              transition: "background 0.15s ease, border-color 0.15s ease",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

